import React from "react";
import Icon from "../../../components/AppIcon";
import { DPAT_BRAND, DPAT_CHART } from "../../dpat-dashboard/dpatChartTheme";

const STATUS_THEME = {
  excellent: { color: DPAT_CHART.emerald, bg: "bg-emerald-500/10", icon: "CheckCircle2", label: "Excellent" },
  good: { color: DPAT_BRAND, bg: "bg-[#16325a]/10", icon: "ThumbsUp", label: "Good" },
  warning: { color: DPAT_CHART.amber, bg: "bg-amber-500/10", icon: "AlertTriangle", label: "Needs attention" },
  critical: { color: DPAT_CHART.coral, bg: "bg-rose-500/10", icon: "AlertCircle", label: "Critical" },
  default: { color: DPAT_CHART.slate, bg: "bg-slate-100", icon: "Info", label: "Info" },
};

const KPIPanel = ({
  kpis,
  title = "Program KPIs",
  subtitle = "Key performance indicators and quality metrics",
}) => {
  return (
    <div className="relative h-full overflow-hidden rounded-2xl border border-[#16325a]/12 bg-white p-4 shadow-[0_8px_24px_rgba(22,50,90,0.05)] md:p-6">
      <div
        className="pointer-events-none absolute -right-10 -top-10 h-36 w-36 rounded-full opacity-40"
        style={{ background: "radial-gradient(circle, rgba(22,50,90,0.12), transparent 70%)" }}
      />
      <div className="relative mb-5">
        <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-[#16325a]/70">
          Performance
        </p>
        <h3 className="mt-1 text-lg font-semibold text-[#16325a] md:text-xl">{title}</h3>
        <p className="mt-1 text-sm text-slate-500">{subtitle}</p>
      </div>

      <div className="relative space-y-4">
        {kpis?.map((kpi, index) => {
          const theme = STATUS_THEME[kpi?.status] || STATUS_THEME.default;
          const numeric = Number.parseFloat(kpi?.value);
          const showBar = Number.isFinite(numeric) && kpi?.unit === "%";

          return (
            <div
              key={index}
              className="rounded-xl border border-slate-100 bg-slate-50/70 p-3.5 transition-colors hover:border-[#16325a]/15 hover:bg-white"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <h4 className="text-sm font-semibold text-slate-800">{kpi?.title}</h4>
                    <span
                      className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold ${theme.bg}`}
                      style={{ color: theme.color }}
                    >
                      <Icon name={theme.icon} size={11} color={theme.color} />
                      {theme.label}
                    </span>
                  </div>
                  <p className="mt-1 text-xs leading-relaxed text-slate-500">{kpi?.description}</p>
                </div>
                <div className="text-right">
                  <div className="text-2xl font-bold tabular-nums text-[#16325a]">
                    {kpi?.value}
                    <span className="ml-0.5 text-sm font-medium text-slate-500">{kpi?.unit}</span>
                  </div>
                </div>
              </div>

              {showBar ? (
                <div className="mt-3 h-1.5 overflow-hidden rounded-full bg-white">
                  <div
                    className="h-full rounded-full transition-all duration-500"
                    style={{
                      width: `${Math.max(0, Math.min(100, numeric))}%`,
                      backgroundColor: theme.color,
                    }}
                  />
                </div>
              ) : null}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default KPIPanel;
